/**
 * Use Case: Fetch Linkable Medias
 *
 * Fetches paginated, linkable medias for the current user.
 * Matches Flutter's getMediasToLink flow from content_data_source.dart.
 *
 * Used by the Link Content sheet inside the publish flow.
 * The current draft's mediaId is excluded client-side so that the draft never
 * shows up as a candidate to link to itself.
 */

import { UserMediaService } from '@/domain/services/userMedia/userMediaService';
import { getAuthFirebaseUserUseCase } from '@/domain/usecases/user/getAuthFirebaseUserUseCase';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import { appLogger } from '@/domain/services/analytics/appLogger';
import type { PageCursor } from '@/data/types/json';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';

export interface FetchLinkableMediasResult {
  medias: UserMediaEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class FetchLinkableMediasUseCase {
  private userMediaService: UserMediaService;

  constructor() {
    this.userMediaService = UserMediaService.getInstance();
  }

  async execute(
    excludeMediaId: string,
    cursor?: PageCursor,
    limit: number = SERVER_FETCH_CONSTANTS.DEFAULT_LIMIT,
  ): Promise<FetchLinkableMediasResult> {
    try {
      const firebaseUser = getAuthFirebaseUserUseCase.execute();
      if (!firebaseUser?.uid) {
        throw new Error('No authenticated user');
      }

      const result = await this.userMediaService.fetchLinkableMedias(
        firebaseUser.uid,
        excludeMediaId,
        limit,
        cursor,
      );

      return {
        medias: result.medias,
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error(
        'Error fetching linkable medias',
        'FetchLinkableMediasUseCase.execute', { error: error as Error },
      );
      throw error;
    }
  }
}

export const createFetchLinkableMediasUseCase =
  (): FetchLinkableMediasUseCase => {
    return new FetchLinkableMediasUseCase();
  };
